import React, { useState } from "react";
import { ContactKind, postContactReveal } from "../../api/AnalyticsAPI";

interface Props {
    /** A search-result row (id, categoryId, storeContact). */
    item: any;
    token?: string;
    /** Compact icon-only buttons for table rows. */
    compact?: boolean;
}

/**
 * The contact actions for one search result: phone/email behind a reveal
 * click, WhatsApp/website as direct links. Every reveal/click fires the
 * contact-reveal beacon (best-effort). Used in the results rows; the detail
 * modal has its own richer layout.
 */
const ContactActions: React.FC<Props> = ({ item, token, compact }) => {
    const contact = item?.storeContact ?? null;
    const [revealed, setRevealed] = useState<Set<ContactKind>>(new Set());

    if (!contact || (!contact.phone && !contact.email && !contact.whatsapp && !contact.website)) {
        return null;
    }

    const beacon = (kind: ContactKind) => {
        postContactReveal(item.id, item.categoryId, kind, token).catch(() => {});
    };
    const reveal = (kind: ContactKind) => {
        if (revealed.has(kind)) return;
        setRevealed(new Set(revealed).add(kind));
        beacon(kind);
    };
    // Contact actions must not open the row's detail modal.
    const stop = (e: React.MouseEvent) => e.stopPropagation();

    return (
        <div onClick={stop} style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem", alignItems: "center" }}>
            {contact.phone && (revealed.has("PHONE") ? (
                <a href={`tel:${contact.phone}`} onClick={() => beacon("PHONE")} style={{ whiteSpace: "nowrap" }}>
                    📞 {contact.phone}
                </a>
            ) : (
                <button type="button" className="btn-sm" onClick={() => reveal("PHONE")} title="Show phone number">
                    <span style={{ fontSize: "1.1em" }}>📞</span> {compact ? "Phone" : "Show phone"}
                </button>
            ))}
            {contact.email && (revealed.has("EMAIL") ? (
                <a href={`mailto:${contact.email}`} style={{ whiteSpace: "nowrap" }}>📧 {contact.email}</a>
            ) : (
                <button type="button" className="btn-sm" onClick={() => reveal("EMAIL")} title="Show email">
                    <span style={{ fontSize: "1.1em" }}>📧</span> {compact ? "Email" : "Show email"}
                </button>
            ))}
            {contact.whatsapp && (
                <a
                    href={`https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`}
                    target="_blank" rel="noopener noreferrer"
                    onClick={() => beacon("WHATSAPP")}
                    title="WhatsApp"
                    style={{ whiteSpace: "nowrap" }}
                >
                    💬 WhatsApp
                </a>
            )}
            {contact.website && (
                <a
                    href={contact.website.startsWith("http") ? contact.website : `https://${contact.website}`}
                    target="_blank" rel="noopener noreferrer"
                    onClick={() => beacon("WEBSITE")}
                    title="Website"
                    style={{ whiteSpace: "nowrap" }}
                >
                    🌐 Website
                </a>
            )}
        </div>
    );
};

export default ContactActions;
